import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import styles from './AffiliateDashboard.module.css';
import { bytesToBase64, encryptForRecipient } from '../utils/hybridCrypto';

type Referral = {
  id: number;
  date: string;
  amount: number;
  status: string;
};

type AffiliateData = {
  affiliateCode: string;
  totalReferrals: number;
  pendingCommissions: number;
  paidCommissions: number;
  recentReferrals: Referral[];
  date_activated: string | null;
};

type Affiliate = {
  email: string;
  code: string;
  date_activated?: string | null;
};

export default function AffiliateDashboard() {
  const { currentUser } = useAuth();

  const userProfileServiceUrl =
    import.meta.env.VITE_USER_PROFILE_SERVICE_URL === 'RELATIVE'
      ? ''
      : import.meta.env.VITE_USER_PROFILE_SERVICE_URL || '';

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [messageName, setMessageName] = useState('');
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const defaultAffiliateData: AffiliateData = { affiliateCode: '', totalReferrals: 0, pendingCommissions: 0, paidCommissions: 0, recentReferrals: [], date_activated: null };

  const { data: affiliateData = defaultAffiliateData, isLoading: loading } = useQuery({
    queryKey: ['affiliateDashboard', currentUser?.email],
    enabled: Boolean(currentUser?.email),
    queryFn: async (): Promise<AffiliateData> => {
      const email = currentUser!.email!;
      const response = await fetch('/api/affiliates');
      if (!response.ok) throw new Error(`Error fetching affiliates: ${response.status}`);
      const affiliates = (await response.json()) as Affiliate[];
      const currentAffiliate = affiliates.find(a => a.email === email);
      if (!currentAffiliate) throw new Error('No affiliate found for current user');
      return {
        affiliateCode: currentAffiliate.code,
        date_activated: currentAffiliate.date_activated ?? null,
        totalReferrals: 3,
        pendingCommissions: 24.99,
        paidCommissions: 49.98,
        recentReferrals: [
          { id: 1, date: '2025-07-01', amount: 24.99, status: 'Pending' },
          { id: 2, date: '2025-06-15', amount: 24.99, status: 'Paid' },
          { id: 3, date: '2025-06-02', amount: 24.99, status: 'Paid' },
        ],
      };
    },
  });

  type CanReceiveResult = { hasKey: boolean; publicId: string | null; publicKeyBase64: string | null; error: string | null };

  const { data: canReceiveData, isLoading: canReceiveLoading } = useQuery({
    queryKey: ['canReceive', currentUser?.uid, userProfileServiceUrl],
    enabled: Boolean(currentUser),
    queryFn: async (): Promise<CanReceiveResult> => {
      const token = await currentUser!.getIdToken().catch(() => null);
      if (!token) return { hasKey: false, publicId: null, publicKeyBase64: null, error: 'Unauthorized. Please sign in again.' };

      const canReceiveResp = await fetch(`${userProfileServiceUrl}/api/user-profiles/me/can-receive`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (canReceiveResp.status === 401) return { hasKey: false, publicId: null, publicKeyBase64: null, error: 'Unauthorized. Please sign in again.' };
      if (!canReceiveResp.ok) return { hasKey: false, publicId: null, publicKeyBase64: null, error: null };

      const canReceiveJson = (await canReceiveResp.json()) as { hasKey?: boolean; publicId?: string | null };
      const hk = Boolean(canReceiveJson?.hasKey);
      const pid = (canReceiveJson?.publicId || '').trim() || null;

      if (!hk || !pid) return { hasKey: hk, publicId: pid, publicKeyBase64: null, error: null };

      const keyResp = await fetch(`${userProfileServiceUrl}/api/user-profiles/public/${encodeURIComponent(pid)}/public-key`);
      if (!keyResp.ok) return { hasKey: hk, publicId: pid, publicKeyBase64: null, error: 'Unable to fetch your public key.' };

      const keyJson = (await keyResp.json()) as { publicKeyBase64?: string };
      const keyB64 = (keyJson?.publicKeyBase64 || '').trim() || null;
      return { hasKey: hk, publicId: pid, publicKeyBase64: keyB64, error: null };
    },
  });

  const hasKey = canReceiveData?.hasKey ?? null;
  const myPublicId = canReceiveData?.publicId ?? null;
  const publicKeyBase64 = canReceiveData?.publicKeyBase64 ?? null;
  const canReceiveError = canReceiveData?.error ?? null;

  const copyToClipboard = (text, linkType = 'generic') => {
    navigator.clipboard.writeText(text);
    console.log(`Copied ${linkType} link: ${text}`);
    alert('Copied to clipboard!');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Affiliate Dashboard</h1>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading your affiliate data...</div>
      ) : (
        <div className={styles.dashboard}>
          <div className={styles.encryptedUploadPanel}>
            <h2 className={styles.encryptedUploadTitle}>Upload an encrypted file to your MPD</h2>

            {canReceiveLoading ? (
              <div className={styles.encryptedUploadHint}>Checking encryption status…</div>
            ) : canReceiveError ? (
              <div className={styles.encryptedUploadError}>{canReceiveError}</div>
            ) : !hasKey || !myPublicId || !publicKeyBase64 ? (
              <div className={styles.encryptedUploadHint}>
                This upload is disabled because your MPD doesn’t have a published encryption key yet.
              </div>
            ) : (
              <div className={styles.encryptedUploadForm}>
                <label className={styles.encryptedUploadField}>
                  <span className={styles.encryptedUploadLabel}>Image file</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={e => {
                      const f = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
                      setSelectedFile(f);
                      setUploadStatus(null);
                      setUploadError(null);
                      if (f && !messageName) {
                        setMessageName(f.name);
                      }
                    }}
                    disabled={uploading}
                  />
                </label>

                <label className={styles.encryptedUploadField}>
                  <span className={styles.encryptedUploadLabel}>Message name</span>
                  <input
                    type="text"
                    value={messageName}
                    onChange={e => setMessageName(e.target.value)}
                    placeholder={selectedFile?.name || 'Optional'}
                    disabled={uploading}
                  />
                </label>

                <button
                  className={styles.encryptedUploadButton}
                  onClick={async () => {
                    setUploadStatus(null);
                    setUploadError(null);

                    if (!currentUser) {
                      setUploadError('You must be signed in.');
                      return;
                    }
                    if (!selectedFile) {
                      setUploadError('Select an image file first.');
                      return;
                    }
                    if (!publicKeyBase64) {
                      setUploadError('Missing public key.');
                      return;
                    }

                    setUploading(true);
                    try {
                      const token = await currentUser.getIdToken();

                      const fileBuf = await selectedFile.arrayBuffer();
                      const fileBytes = new Uint8Array(fileBuf);
                      const meta = {
                        messageType: 'image_file',
                        mimeType: selectedFile.type || 'application/octet-stream',
                        fileName: selectedFile.name,
                      };

                      const headerBytes = new TextEncoder().encode(JSON.stringify(meta));
                      const payload = new Uint8Array(headerBytes.length + 1 + fileBytes.length);
                      payload.set(headerBytes, 0);
                      payload[headerBytes.length] = 0x0a;
                      payload.set(fileBytes, headerBytes.length + 1);

                      console.log('Encrypting with publicKeyBase64, length=' + publicKeyBase64.length + ', first40=' + publicKeyBase64.substring(0, 40));
                      const encrypted = await encryptForRecipient(publicKeyBase64, payload);
                      const encryptedBase64 = bytesToBase64(encrypted);

                      const resp = await fetch(`${userProfileServiceUrl}/api/user-profiles/me/messages`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                          name: messageName || selectedFile.name,
                          messageType: 'image_file',
                          mimeType: selectedFile.type || 'application/octet-stream',
                          fileName: selectedFile.name,
                          encryptedBytesBase64: encryptedBase64,
                        }),
                      });

                      if (!resp.ok) {
                        setUploadError(`Upload failed (${resp.status}).`);
                        return;
                      }

                      const json = (await resp.json()) as { messageId?: string };
                      setUploadStatus(`Uploaded encrypted message${json?.messageId ? ` (id ${json.messageId})` : ''}.`);
                      setSelectedFile(null);
                    } catch (err) {
                      console.error('Encrypted upload failed:', err);
                      setUploadError('Encrypt & upload failed.');
                    } finally {
                      setUploading(false);
                    }
                  }}
                  disabled={uploading}
                >
                  {uploading ? 'Encrypting…' : 'Encrypt & Upload'}
                </button>

                {uploadStatus && <div className={styles.encryptedUploadSuccess}>{uploadStatus}</div>}
                {uploadError && <div className={styles.encryptedUploadError}>{uploadError}</div>}
              </div>
            )}
          </div>

          <div className={styles.activationStatus}>
            {affiliateData.date_activated ? (
              <div className={styles.statusContainer}>
                <span className={styles.statusDot + ' ' + styles.activeDot}></span>
                <span className={styles.activated}>
                  Active since {new Date(affiliateData.date_activated).toLocaleDateString()}
                </span>
              </div>
            ) : (
              <div className={styles.statusContainer}>
                <span className={styles.statusDot + ' ' + styles.inactiveDot}></span>
                <span className={styles.inactive}>Not Activated</span>
              </div>
            )}
          </div>
          {affiliateData.date_activated && (
            <div className={styles.statsSection}>
              <div className={styles.statCard}>
                <h3>Your Affiliate Code</h3>
                <div className={styles.affiliateCode}>
                  <span>{affiliateData.affiliateCode}</span>
                  <button
                    onClick={() => copyToClipboard(affiliateData.affiliateCode, 'code')}
                    className={styles.copyButton}
                  >
                    Copy
                  </button>
                </div>
                <div className={styles.affiliateLinkActions}>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `${window.location.origin}/buy?ref=${affiliateData.affiliateCode}`,
                        'buy-link'
                      )
                    }
                    className={styles.linkButton}
                  >
                    Copy Buy Link
                  </button>
                </div>
              </div>

              <div style={{ display: 'none' }}>
                <div className={styles.statCard}>
                  <h3>Total Referrals</h3>
                  <p className={styles.statValue}>{affiliateData.totalReferrals}</p>
                </div>

                <div className={styles.statCard}>
                  <h3>Pending Commissions</h3>
                  <p className={styles.statValue}>${affiliateData.pendingCommissions.toFixed(2)}</p>
                </div>

                <div className={styles.statCard}>
                  <h3>Paid Commissions</h3>
                  <p className={styles.statValue}>${affiliateData.paidCommissions.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          <div className={styles.referralsSection}>
            <div className={styles.comingSoonWatermark}>COMING SOON</div>
            <h2>Recent Referrals</h2>

            <div className={styles.comingSoonContent}>
              <p>Track your referrals and commissions</p>
              <p>Monitor conversion rates</p>
              <p>View payment history</p>
            </div>
          </div>

          {affiliateData.date_activated && (
            <div className={styles.marketingSection}>
              <h2>Marketing Materials</h2>
              <div className={styles.marketingCards}>
                <div className={styles.marketingCard}>
                  <h3>Affiliate Link</h3>
                  <p>Share this link with your audience:</p>
                  <div className={styles.affiliateLink}>
                    <input
                      type="text"
                      value={`https://mypeopledatabase.com/?ref=${affiliateData.affiliateCode}`}
                      readOnly
                    />
                    <button
                      onClick={() =>
                        copyToClipboard(
                          `https://mypeopledatabase.com/?ref=${affiliateData.affiliateCode}`,
                          'homepage'
                        )
                      }
                      className={styles.copyButton}
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className={styles.marketingCard}>
                  <h3>Buy Page Link</h3>
                  <p>Direct link to purchase with your code:</p>
                  <div className={styles.affiliateLink}>
                    <input
                      type="text"
                      value={`${window.location.origin}/buy?ref=${affiliateData.affiliateCode}`}
                      readOnly
                    />
                    <button
                      onClick={() =>
                        copyToClipboard(
                          `${window.location.origin}/buy?ref=${affiliateData.affiliateCode}`,
                          'buy-page'
                        )
                      }
                      className={styles.copyButton}
                    >
                      Copy Link
                    </button>
                  </div>
                </div>

                {/*<div className={styles.marketingCard}>
                  <h3>Promo Graphics</h3>
                  <p>Download banners and graphics:</p>
                  <div className={styles.downloadButtons}>
                    <button className={styles.downloadButton}>Banner (1200×628)</button>
                    <button className={styles.downloadButton}>Square (1080×1080)</button>
                    <button className={styles.downloadButton}>Logo Pack</button>
                  </div>
                </div>*/}
              </div>
            </div>
          )}

          <div className={styles.paymentSection}>
            <h2>Payment Information</h2>
            <p>Commissions are calculated monthly and paid the following month.</p>
            <p>
              Email <a href="mailto:support@mypeopledatabase.com">support@mypeopledatabase.com</a>{' '}
              to setup payments
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
